import 'src/global.css';

import { useEffect } from 'react';

import { usePathname } from 'src/routes/hooks';

import { useMatomo } from 'src/hooks/use-matomo';

import Bridge from 'src/bridge';
import { CONFIG } from 'src/global-config';
import { hideDialog } from 'src/state/ui/actions';
import { LocalizationProvider } from 'src/locales';
import { appNavigated } from 'src/state/app/action';
import { themeConfig, ThemeProvider } from 'src/theme';
import { getShowDialog } from 'src/state/ui/selectors';
import { I18nProvider } from 'src/locales/i18n-provider';
import { loadWorkspaces } from 'src/state/workspace/actions';
import { useSelector, useDispatch, type RootState } from 'src/state';

import { Snackbar } from 'src/components/snackbar';
import { ProgressBar } from 'src/components/progress-bar';
import { MotionLazy } from 'src/components/animate/motion-lazy';
// import { IntercomProvider } from 'src/components/intercom';
import { FeaturebaseProvider } from 'src/components/featurebase';
import { SubscriptionDialog } from 'src/components/subscription';
import { SettingsDrawer, defaultSettings, SettingsProvider } from 'src/components/settings';

import { FolderDialog } from 'src/sections/forms/folder-dialog';
import { CheckoutProvider } from 'src/sections/checkout/context';

import { AuthProvider as JwtAuthProvider } from 'src/auth/context/jwt';
import { AuthProvider as Auth0AuthProvider } from 'src/auth/context/auth0';
import { AuthProvider as AmplifyAuthProvider } from 'src/auth/context/amplify';
import { AuthProvider as SupabaseAuthProvider } from 'src/auth/context/supabase';
import { AuthProvider as FirebaseAuthProvider } from 'src/auth/context/firebase';

// ----------------------------------------------------------------------

const AuthProvider =
  (CONFIG.auth.method === 'amplify' && AmplifyAuthProvider) ||
  (CONFIG.auth.method === 'firebase' && FirebaseAuthProvider) ||
  (CONFIG.auth.method === 'supabase' && SupabaseAuthProvider) ||
  (CONFIG.auth.method === 'auth0' && Auth0AuthProvider) ||
  JwtAuthProvider;

// ----------------------------------------------------------------------

type AppProps = {
  children: React.ReactNode;
};

export default function App({ children }: AppProps) {
  useScrollToTop();
  useMatomo();
  useNavigationTracking(); // Track URL navigation in Redux

  const dispatch = useDispatch();
  const showSubscriptionDialog = useSelector((state: RootState) =>
    getShowDialog(state, 'subscription')
  );

  // Load workspaces on mount
  useEffect(() => {
    dispatch(loadWorkspaces());
  }, [dispatch]);

  useEffect(() => {
    console.log('App: showSubscriptionDialog state changed:', showSubscriptionDialog);
  }, [showSubscriptionDialog]);

  const handleCloseSubscriptionDialog = () => {
    dispatch(hideDialog('subscription'));
  };

  return (
    <I18nProvider>
      <AuthProvider>
        <SettingsProvider defaultSettings={defaultSettings}>
          <LocalizationProvider>
            <ThemeProvider
              modeStorageKey={themeConfig.modeStorageKey}
              defaultMode={themeConfig.defaultMode}
            >
              <MotionLazy>
                <CheckoutProvider>
                  <Bridge />
                  {/* <IntercomProvider /> */}
                  <FeaturebaseProvider />
                  <Snackbar />
                  <ProgressBar />
                  <SettingsDrawer defaultSettings={defaultSettings} />
                  <SubscriptionDialog
                    open={showSubscriptionDialog}
                    onClose={handleCloseSubscriptionDialog}
                  />
                  <FolderDialog />
                  {children}
                </CheckoutProvider>
              </MotionLazy>
            </ThemeProvider>
          </LocalizationProvider>
        </SettingsProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

// ----------------------------------------------------------------------

function useScrollToTop() {
  const pathname = usePathname();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function useNavigationTracking() {
  const pathname = usePathname();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(appNavigated(pathname));
  }, [pathname, dispatch]);

  return null;
}
